import { useMemo } from 'react';
import {
  formatProductDiscountText,
  getBogoFromProduct,
  getBogoSpecialURL,
  getBundleFromProduct,
} from '@/utils';
import { GProduct, Variant } from '@/types';
import { useData } from '../components';

const useActiveProductBadges = ({
  product,
  weightVariant,
  isDetail = false,
  badgeClass,
}: ActiveBadgesParams) => {
  const { productCard } = useData();
  const bogo = getBogoFromProduct(product);
  const bundle = getBundleFromProduct(product);

  const productDiscountText = useMemo(
    () =>
      formatProductDiscountText({
        format: productCard?.discount_display ?? 'percentage',
        value: weightVariant,
      }),
    [weightVariant, productCard?.discount_display],
  );

  const hasStockFlags = !!(
    product?.isLowStock ||
    product?.isOutStock ||
    productDiscountText
  );

  const hasPromotions = !!(bogo || bundle);

  const showPromoBadges = !isDetail && hasPromotions;

  const shouldRender = hasStockFlags || showPromoBadges;

  const badges = useMemo(() => {
    const allBadgeConfigs: (BadgeConfig & { condition: boolean })[] = [
      {
        condition: !!product.isLowStock,
        props: {
          key: 'badge-low-stock',
          label: 'Low Stock',
          type: 'warning',
        },
      },
      {
        condition: !!product.isOutStock,
        props: {
          key: 'badge-out-of-stock',
          label: 'Out of Stock',
          type: 'warning',
        },
      },
      {
        condition: !isDetail && !!bogo,
        props: {
          key: 'badge-bogo',
          label: productCard?.bogo_badge_label ?? 'BOGO',
          type: 'bogo',
          iconName: 'gift',
          link: bogo ? getBogoSpecialURL(bogo) : undefined,
          showIcon: true,
        },
      },
      {
        condition: !isDetail && !!bundle,
        props: {
          key: 'badge-bundle',
          label: productCard?.bundle_badge_label ?? 'BUNDLE',
          type: 'bogo',
          iconName: 'price-tag',
          link: bundle ? getBogoSpecialURL(bundle) : undefined,
          showIcon: true,
        },
      },
      {
        condition: !!productDiscountText,
        props: {
          key: 'badge-discount',
          label: productDiscountText || '',
          type: 'discount',
        },
      },
    ];

    return allBadgeConfigs
      .filter(badge => badge.condition)
      .map(({ props }) => ({
        props: {
          ...props,
          className: badgeClass,
        },
      }));
  }, [
    product.isLowStock,
    product.isOutStock,
    bogo,
    bundle,
    productDiscountText,
    isDetail,
    badgeClass,
    productCard?.bogo_badge_label,
    productCard?.bundle_badge_label,
  ]);

  return {
    shouldRender,
    badges,
  };
};

interface ActiveBadgesParams {
  product: GProduct;
  weightVariant: Variant;
  isDetail?: boolean;
  badgeClass?: string;
}

interface BadgeConfig {
  props: {
    className?: string;
    key: string;
    label: string;
    type: 'warning' | 'bogo' | 'discount';
    iconName?: 'gift' | 'price-tag';
    link?: string;
    showIcon?: boolean;
  };
}

export { useActiveProductBadges };
